import { useState, useEffect, useRef } from 'react';
import ApexCharts from 'apexcharts';

const CHART_CONFIG = {
  periods: { '1m': '1개월', '3m': '3개월', '1y': '1년' },
  height: 350
};

const API_BASE = 'https://koreakookmingold.com/api/chart';

export default function PriceChart({ metalType, title, unit }) {
  const [activePeriod, setActivePeriod] = useState('3m');
  const [loading, setLoading] = useState(false);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const sortedDataRef = useRef([]);

  const fetchData = async (period) => {
    if (!['gold', 'silver'].includes(metalType) || !['1m', '3m', '1y'].includes(period)) return null;
    
    try {
      setLoading(true);
      const endDate = new Date();
      const startDate = new Date();
      
      if (period === '1m') startDate.setMonth(startDate.getMonth() - 1);
      else if (period === '3m') startDate.setMonth(startDate.getMonth() - 3);
      else if (period === '1y') startDate.setFullYear(startDate.getFullYear() - 1);
      
      const url = new URL(API_BASE);
      url.searchParams.set('startDate', startDate.toISOString().split('T')[0]);
      url.searchParams.set('endDate', endDate.toISOString().split('T')[0]);
      
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const result = await response.json();
      if (!result?.history?.length) return null;
      
      const buyField = metalType === 'gold' ? 'sell24k' : 'sellSilver';
      const sellField = metalType === 'gold' ? 'buy24k' : 'buySilver';
      
      const sortedData = result.history.sort((a, b) => new Date(a.date) - new Date(b.date));
      sortedDataRef.current = sortedData;
      
      return {
        dates: sortedData.map(item => {
          const d = new Date(item.date);
          return `${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getDate().toString().padStart(2, '0')}`;
        }),
        buyPrices: sortedData.map(item => item[buyField]),
        sellPrices: sortedData.map(item => item[sellField])
      };
    } catch (error) {
      console.error(`Chart fetch error: ${error.message}`);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const createChart = (data) => {
    if (!chartRef.current || !data) return;

    const width = window.innerWidth;
    const isMobile = width <= 576;

    const options = {
      series: [
        {
          name: `살 때(${unit})`,
          data: data.buyPrices
        },
        {
          name: `팔 때(${unit})`,
          data: data.sellPrices
        }
      ],
      chart: {
        type: 'area',
        height: CHART_CONFIG.height,
        zoom: {
          enabled: false
        },
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800
        }
      },
      colors: ['#ef892d', '#8bca38'],
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: 'smooth',
        width: 1
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 100]
        }
      },
      xaxis: {
        categories: data.dates,
        labels: {
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true,
          showDuplicates: false,
          trim: true,
          style: {
            colors: '#8e8da4',
            fontSize: '11px'
          }
        },
        tickAmount: 6,
        tooltip: {
          enabled: false
        }
      },
      yaxis: {
        labels: {
          style: {
            colors: '#8e8da4',
            fontSize: '11px'
          },
          formatter: (value) => isMobile ? (value / 1000).toFixed(0) + 'k' : value.toLocaleString(),
          offsetX: isMobile ? -5 : 0
        }
      },
      grid: {
        borderColor: '#e0e0e0',
        strokeDashArray: 0,
        padding: {
          left: isMobile ? 10 : 5,
          right: isMobile ? 20 : 5
        }
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '12px',
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontWeight: 400,
        labels: {
          colors: '#373d3f'
        }
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: {
          show: false
        },
        custom: function({ series, seriesIndex, dataPointIndex, w }) {
          const item = sortedDataRef.current[dataPointIndex];
          if (!item) return '';
          
          const d = new Date(item.date);
          const dateStr = `${d.getFullYear()}년 ${(d.getMonth() + 1).toString().padStart(2, '0')}월 ${d.getDate().toString().padStart(2, '0')}일`;
          
          let content = `<div style="padding: 8px 10px; background: #fff; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
              <div style="font-weight: 600; margin-bottom: 5px; color: #333; font-size: 11px; white-space: nowrap;">${dateStr}</div>`;
          
          if (series[0] && series[0][dataPointIndex] !== undefined) {
            content += `<div style="display: flex; align-items: center; margin-bottom: 3px;">
                <span style="width: 8px; height: 8px; background: #ef892d; border-radius: 50%; margin-right: 6px; flex-shrink: 0;"></span>
                <span style="color: #666; font-size: 10px; white-space: nowrap;">살 때: <strong style="color: #333;">${series[0][dataPointIndex].toLocaleString()}원</strong></span>
              </div>`;
          }
          
          if (series[1] && series[1][dataPointIndex] !== undefined) {
            content += `<div style="display: flex; align-items: center;">
                <span style="width: 8px; height: 8px; background: #8bca38; border-radius: 50%; margin-right: 6px; flex-shrink: 0;"></span>
                <span style="color: #666; font-size: 10px; white-space: nowrap;">팔 때: <strong style="color: #333;">${series[1][dataPointIndex].toLocaleString()}원</strong></span>
              </div>`;
          }
          
          content += `</div>`;
          return content;
        }
      }
    };

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    chartInstance.current = new ApexCharts(chartRef.current, options);
    chartInstance.current.render();
  };

  const handlePeriodChange = async (period) => {
    if (period === activePeriod || loading) return;
    
    setActivePeriod(period);
    const newData = await fetchData(period);
    if (newData) {
      createChart(newData);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      const initialData = await fetchData('3m');
      if (initialData) {
        createChart(initialData);
      }
    };
    loadInitialData();

    const chartElement = chartRef.current;
    const preventScroll = (e) => {
      e.stopPropagation();
    };

    if (chartElement) {
      chartElement.addEventListener('touchstart', preventScroll, { passive: true });
      chartElement.addEventListener('touchmove', preventScroll, { passive: true });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
      if (chartElement) {
        chartElement.removeEventListener('touchstart', preventScroll);
        chartElement.removeEventListener('touchmove', preventScroll);
      }
    };
  }, [metalType]);

  return (
    <div className="price-graph__box">
      <div className="price-graph__top">
        <h3 className="price-graph__title">
          {title} <span className="price-graph__title--unit">({unit})</span>
        </h3>
        <a href="/market-price" className="main-more">
          <span className="main-more--underline">View more</span>
        </a>
      </div>
      <div className="price-graph__btm">
        <ul className="price-graph__tablist">
          {Object.entries(CHART_CONFIG.periods).map(([period, label]) => (
            <li key={period} className={`price-graph__tabitem ${activePeriod === period ? 'price-graph__tabitem--active' : ''}`}>
              <button 
                className="price-graph__tablink" 
                onClick={() => handlePeriodChange(period)}
                disabled={loading}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
        <div ref={chartRef} className="price-graph__table" style={{ touchAction: 'pan-y' }} />
        {loading && <div className="chart-loading">Loading...</div>}
      </div>
    </div>
  );
}